import { useState, useEffect } from 'react';
import { ListItem, Tooltip, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Box, Typography } from '@mui/material';
import { Language, ExpandMore, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

// src/LanguageSwitcher/LanguageSwitcher.tsx
var DEFAULT_LANGUAGES = [
  { code: "en", label: "English", shortLabel: "EN" },
  { code: "th", label: "\u0E20\u0E32\u0E29\u0E32\u0E44\u0E17\u0E22", shortLabel: "TH" },
  { code: "my", label: "\u1019\u103C\u1014\u103A\u1019\u102C\u1018\u102C\u101E\u102C", shortLabel: "MY" }
];
var LanguageSwitcher = ({
  variant = "button",
  isWide = true,
  sx,
  className,
  languages = DEFAULT_LANGUAGES,
  onChange,
  showLabel = true
}) => {
  const { i18n } = useTranslation();
  const getInitialLang = () => {
    try {
      const current = i18n?.language || localStorage.getItem("i18nextLng") || "th";
      return current.split("-")[0] || "th";
    } catch {
      return "th";
    }
  };
  const [currentLanguage, setCurrentLanguage] = useState(getInitialLang);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  useEffect(() => {
    if (i18n?.language) {
      setCurrentLanguage(i18n.language.split("-")[0] || "th");
    }
  }, [i18n?.language]);
  const handleLanguageChange = async (newLng) => {
    setCurrentLanguage(newLng);
    try {
      localStorage.setItem("i18nextLng", newLng);
    } catch {
    }
    if (i18n?.changeLanguage) {
      await i18n.changeLanguage(newLng);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = newLng;
    }
    if (onChange) {
      onChange(newLng);
    }
    setAnchorEl(null);
  };
  const currentOption = languages.find((l) => l.code === currentLanguage) || languages[0] || { label: currentLanguage, shortLabel: currentLanguage.toUpperCase() };
  if (variant === "sidebar") {
    const collapsedButtonSx = {
      p: 1,
      justifyContent: isWide ? "initial" : "center"
    };
    const collapsedIconSx = {
      minWidth: isWide ? 56 : 0,
      justifyContent: isWide ? "initial" : "center",
      alignItems: "center",
      display: "flex"
    };
    return /* @__PURE__ */ jsxs(ListItem, { disablePadding: true, sx: { width: "100%", ...sx }, children: [
      /* @__PURE__ */ jsx(Tooltip, { title: !isWide ? `Language: ${currentOption.label}` : "", placement: "right", children: /* @__PURE__ */ jsxs(
        ListItemButton,
        {
          onClick: (e) => setAnchorEl(e.currentTarget),
          sx: collapsedButtonSx,
          "aria-haspopup": "true",
          "aria-expanded": openMenu ? "true" : void 0,
          children: [
            /* @__PURE__ */ jsx(ListItemIcon, { sx: collapsedIconSx, children: /* @__PURE__ */ jsx(Language, {}) }),
            /* @__PURE__ */ jsx(
              ListItemText,
              {
                primary: currentOption.label,
                secondary: `(${currentOption.shortLabel})`,
                secondaryTypographyProps: { fontSize: "0.75rem", color: "text.secondary" },
                sx: { display: isWide ? "block" : "none", my: 0 }
              }
            ),
            isWide && /* @__PURE__ */ jsx(ExpandMore, { fontSize: "small", sx: { ml: "auto", color: "action.active" } })
          ]
        }
      ) }),
      /* @__PURE__ */ jsx(
        Menu,
        {
          anchorEl,
          open: openMenu,
          onClose: () => setAnchorEl(null),
          anchorOrigin: {
            vertical: "center",
            horizontal: isWide ? "right" : "right"
          },
          transformOrigin: {
            vertical: "center",
            horizontal: "left"
          },
          slotProps: {
            paper: {
              sx: {
                minWidth: 150,
                boxShadow: 3,
                borderRadius: 2,
                mt: 0
              }
            }
          },
          children: languages.map((lang) => /* @__PURE__ */ jsxs(
            MenuItem,
            {
              selected: lang.code === currentLanguage,
              onClick: () => handleLanguageChange(lang.code),
              sx: {
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                py: 1,
                fontSize: "0.875rem"
              },
              children: [
                /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
                  /* @__PURE__ */ jsx("span", { children: lang.label }),
                  /* @__PURE__ */ jsxs(Typography, { variant: "caption", sx: { color: "text.secondary", fontWeight: 600 }, children: [
                    "(",
                    lang.shortLabel,
                    ")"
                  ] })
                ] }),
                lang.code === currentLanguage && /* @__PURE__ */ jsx(Check, { fontSize: "small", color: "primary" })
              ]
            },
            lang.code
          ))
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Tooltip, { title: `Language: ${currentOption.label} (${currentOption.shortLabel})`, placement: "top", children: /* @__PURE__ */ jsxs(
      Box,
      {
        component: "button",
        type: "button",
        onClick: (e) => setAnchorEl(e.currentTarget),
        className,
        "aria-haspopup": "true",
        "aria-expanded": openMenu ? "true" : void 0,
        sx: {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: showLabel ? 1.5 : 1,
          py: 0.75,
          minHeight: 36,
          minWidth: showLabel ? void 0 : 36,
          cursor: "pointer",
          borderRadius: 2,
          bgcolor: "white",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          boxShadow: 2,
          outline: "none",
          color: "text.primary",
          userSelect: "none",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: 4,
            bgcolor: "#f8fafc"
          },
          ...sx
        },
        children: [
          /* @__PURE__ */ jsx(Language, { sx: { fontSize: 20, color: "text.secondary" } }),
          showLabel && /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 600, fontSize: "0.875rem" }, children: currentOption.shortLabel })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(
      Menu,
      {
        anchorEl,
        open: openMenu,
        onClose: () => setAnchorEl(null),
        anchorOrigin: {
          vertical: "top",
          horizontal: "left"
        },
        transformOrigin: {
          vertical: "bottom",
          horizontal: "left"
        },
        slotProps: {
          paper: {
            sx: {
              minWidth: 160,
              boxShadow: 4,
              borderRadius: 2,
              mb: 1
            }
          }
        },
        children: languages.map((lang) => /* @__PURE__ */ jsxs(
          MenuItem,
          {
            selected: lang.code === currentLanguage,
            onClick: () => handleLanguageChange(lang.code),
            sx: {
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              py: 1,
              fontSize: "0.875rem"
            },
            children: [
              /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
                /* @__PURE__ */ jsx("span", { children: lang.label }),
                /* @__PURE__ */ jsxs(Typography, { variant: "caption", sx: { color: "text.secondary", fontWeight: 600 }, children: [
                  "(",
                  lang.shortLabel,
                  ")"
                ] })
              ] }),
              lang.code === currentLanguage && /* @__PURE__ */ jsx(Check, { fontSize: "small", color: "primary" })
            ]
          },
          lang.code
        ))
      }
    )
  ] });
};
var LanguageSwitcher_default = LanguageSwitcher;

export { DEFAULT_LANGUAGES, LanguageSwitcher, LanguageSwitcher_default };
//# sourceMappingURL=chunk-PASAUMN2.mjs.map
//# sourceMappingURL=chunk-PASAUMN2.mjs.map